import React, { FunctionComponent, useEffect, useState } from "react";
import {
    EuiButton,
    EuiButtonEmpty,
    EuiButtonGroup,
    EuiFieldText,
    EuiFlexGroup,
    EuiFlexItem,
    EuiPanel,
    EuiText
} from "@elastic/eui";
import {
    addExtraTrafficServer,
    ExtraTrafficApp,
    ExtraTrafficServer,
    listenExtraTrafficServers,
    normalizeTrafficHost,
    removeExtraTrafficServer
} from "../application/FirebaseConfigs";
import { BMServerTraffic } from "../model/BMServerTraffic";

interface IProps {
    traffic: BMServerTraffic
    onChanged: () => void
}

const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
const DOMAIN = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

const appOptions = [
    { id: "spl", label: "SPL" },
    { id: "karak", label: "Karak" }
];

export const ExtraTrafficServers: FunctionComponent<IProps> = (props) => {
    const [servers, setServers] = useState<ExtraTrafficServer[]>([]);
    const [hostInput, setHostInput] = useState("");
    const [app, setApp] = useState<ExtraTrafficApp>("spl");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => listenExtraTrafficServers(setServers), []);

    const add = async () => {
        const host = normalizeTrafficHost(hostInput);
        if (!IPV4.test(host) && !DOMAIN.test(host)) {
            setError("Enter a valid IPv4 address or domain");
            return;
        }
        if (servers.some(s => s.host === host)) {
            setError(host + " is already added");
            return;
        }
        setError("");
        setBusy(true);
        try {
            await addExtraTrafficServer(host, app);
            setHostInput("");
            props.onChanged();
        } catch (e: any) {
            setError("Could not save: " + (e?.message || e));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (host: string) => {
        setBusy(true);
        try {
            await removeExtraTrafficServer(host);
            props.onChanged();
        } catch (e: any) {
            setError("Could not remove: " + (e?.message || e));
        } finally {
            setBusy(false);
        }
    };

    const status = (host: string) => {
        const reported = (props.traffic.extraServers || []).find(e => e.host === host);
        const seen = props.traffic.servers && props.traffic.servers.has(host);
        if (reported && seen) return (reported.traffic / 1024).toFixed(2) + " MBps";
        if (reported) return "no netdata response";
        if (seen) return "already in Remote Config, not double-counted";
        return "waiting for next refresh";
    };

    return (
        <EuiPanel paddingSize="s" hasBorder>
            <EuiText size="s"><b>Manual netdata servers</b></EuiText>
            <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
                <EuiFlexItem>
                    <EuiFieldText
                        compressed
                        placeholder="IP or domain"
                        value={hostInput}
                        isInvalid={!!error}
                        onChange={e => setHostInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") add(); }}
                    />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <EuiButtonGroup
                        legend="Count traffic as"
                        buttonSize="compressed"
                        options={appOptions}
                        idSelected={app}
                        onChange={id => setApp(id as ExtraTrafficApp)}
                    />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                    <EuiButton size="s" fill onClick={add} isLoading={busy}>Add</EuiButton>
                </EuiFlexItem>
            </EuiFlexGroup>
            {error ? <EuiText size="xs" color="danger">{error}</EuiText> : null}
            {servers.map(s => (
                <EuiFlexGroup key={s.host} gutterSize="s" alignItems="center" responsive={false}>
                    <EuiFlexItem>
                        <EuiText size="xs">{s.host} · {s.app === "karak" ? "Karak" : "SPL"} · {status(s.host)}</EuiText>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                        <EuiButtonEmpty size="xs" color="danger" onClick={() => remove(s.host)} isDisabled={busy}>Remove</EuiButtonEmpty>
                    </EuiFlexItem>
                </EuiFlexGroup>
            ))}
        </EuiPanel>
    );
};
